import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import {
  RMSTextField,
  RMSSelect,
  RMSDateTimePicker,
} from '../../../shared/components/form-controls';
import SearchPersonAutoComplete from '../../../shared/components/modules/SearchPersonAutoComplete';
import SearchAddressAutoComplete from '../../../shared/components/modules/SearchAddressAutoComplete';
import SearchVehicleAutoComplete from '../../../shared/components/modules/SearchVehicleAutoComplete';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import {
  BULLETIN_DEFAULT_VALUES,
  DICT_SELECT_PROPS,
  AGENCY_SELECT_PROPS,
  PRIORITY_OPTIONS,
  PRIORITY_SELECT_PROPS,
} from '../../constants/personBulletinFormConstants';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import { createBulletin, updateBulletin } from '../../services/userService';
import AddPersonDialog from './AddPersonDialog';
import AddAddressDialog from './AddAddressDialog';
import AddVehicleDialog from './AddVehicleDialog';

const PersonBulletinDialog = ({ open, onClose, personId, bulletin = null, onSaved }) => {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const [addPersonDialogOpen, setAddPersonDialogOpen] = useState(false);
  const [addAddressDialogOpen, setAddAddressDialogOpen] = useState(false);
  const [addVehicleDialogOpen, setAddVehicleDialogOpen] = useState(false);
  const isEditMode = Boolean(bulletin?.ptsAlertID);
  const { alertTypeOptions, agencyOptions, stateOptions } = useUserDictionaryOptions();

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: isEditMode
      ? {
          AlertType: bulletin.AlertType || '',
          Flags: bulletin.Flags || null,
          AgencyID: bulletin.AgencyID || '',
          Priority: bulletin.Priority || '1',
          Beginning: bulletin.Beginning || null,
          Ending: bulletin.Ending || null,
          RangeOfEffectiveness: bulletin.RangeOfEffectiveness || '',
          ptsPersonID: bulletin.ptsPersonID || null,
          ptsAddressID: bulletin.ptsAddressID || null,
          ptsVehicleID: bulletin.ptsVehicleID || null,
          Plate: bulletin.Plate || '',
          PlateState: bulletin.PlateState || '',
          OLN: bulletin.OLN || '',
          OLNState: bulletin.OLNState || '',
          Notification: bulletin.Notification || '',
        }
      : { ...BULLETIN_DEFAULT_VALUES, ptsPersonID: personId },
  });

  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const ptsPersonID = useWatch({ control: methods.control, name: 'ptsPersonID' });
  const ptsAddressID = useWatch({ control: methods.control, name: 'ptsAddressID' });
  const ptsVehicleID = useWatch({ control: methods.control, name: 'ptsVehicleID' });
  const beginning = useWatch({ control: methods.control, name: 'Beginning' });
  const ending = useWatch({ control: methods.control, name: 'Ending' });

  const handlePersonCreated = useCallback(
    (person) => {
      methods.setValue('ptsPersonID', person?.ptsPersonID ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setAddPersonDialogOpen(false);
    },
    [methods]
  );

  const handleAddressCreated = useCallback(
    (address) => {
      methods.setValue('ptsAddressID', address?.ptsAddressID ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setAddAddressDialogOpen(false);
    },
    [methods]
  );

  const handleAlertTypeChange = useCallback(
    (e, option) => {
      methods.setValue('Flags', option?.Flags ?? null);
    },
    [methods]
  );

  const handleVehicleSelect = useCallback(
    (vehicle) => {
      if (!vehicle) return;

      if (vehicle.PlateNumber) methods.setValue('Plate', vehicle.PlateNumber);
      if (vehicle.PlateState) methods.setValue('PlateState', vehicle.PlateState);
    },
    [methods]
  );

  const handleVehicleCreated = useCallback(
    ({ vehicle, registration } = {}) => {
      methods.setValue('ptsVehicleID', vehicle?.ptsVehicleID ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });

      if (registration?.PlateNumber) methods.setValue('Plate', registration.PlateNumber);
      if (registration?.PlateState) methods.setValue('PlateState', registration.PlateState);

      setAddVehicleDialogOpen(false);
    },
    [methods]
  );

  const onSubmit = useCallback(
    async (data) => {
      const payload = {
        AlertType: data.AlertType || null,
        Flags: data.Flags || null,
        Beginning: data.Beginning || null,
        Ending: data.Ending || null,
        Notification: data.Notification || null,
        ptsPersonID: data.ptsPersonID || null,
        ptsAddressID: data.ptsAddressID || null,
        ptsVehicleID: data.ptsVehicleID || null,
        Plate: data.Plate || null,
        PlateState: data.PlateState || null,
        OLN: data.OLN || null,
        OLNState: data.OLNState || null,
        AgencyID: data.AgencyID || null,
        Priority: data.Priority || null,
        RangeOfEffectiveness: data.RangeOfEffectiveness || null,
      };

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () =>
          isEditMode ? updateBulletin(bulletin.ptsAlertID, payload) : createBulletin(payload),
        onSuccess: () => {
          dispatch(
            notify(
              isEditMode ? 'Bulletin updated successfully' : 'Bulletin created successfully',
              'success'
            )
          );
          onSaved?.();
        },
      });
    },
    [dispatch, isEditMode, bulletin, onSaved]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this bulletin. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  return (
    <RMSCommonDialog
      open={open}
      title={isEditMode ? 'Edit Bulletin' : 'Add Bulletin'}
      onClose={requestClose}
      onSave={handleSave}
      loading={submitting}
      saveDisabled={submitting}
      maxWidth="md"
      fullWidth
      closeOnBackdropClick={false}
      saveButton={{
        show: true,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}
      leftActions={
        isEditMode && (
          <RMSRecordHistoryButton
            disabled={!bulletin?.Created}
            created={bulletin?.Created}
            createdBy={bulletin?.CreatedBy}
            updated={bulletin?.Updated}
            updatedBy={bulletin?.UpdatedBy}
          />
        )
      }>
      <FormProvider {...methods}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <RMSSelect
              name="AlertType"
              label="Bulletin Type"
              options={alertTypeOptions}
              {...DICT_SELECT_PROPS}
              onChange={handleAlertTypeChange}
            />
          </Grid>
          <Grid item xs={8}>
            <RMSSelect
              name="AgencyID"
              label="Agency"
              options={agencyOptions}
              {...AGENCY_SELECT_PROPS}
            />
          </Grid>
          <Grid item xs={4}>
            <RMSSelect
              name="Priority"
              label="Priority"
              options={PRIORITY_OPTIONS}
              {...PRIORITY_SELECT_PROPS}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSDateTimePicker name="Beginning" label="Date From" maxDate={ending || undefined} />
          </Grid>
          <Grid item xs={6}>
            <RMSDateTimePicker name="Ending" label="Date To" minDate={beginning || undefined} />
          </Grid>
          <Grid item xs={12}>
            <RMSTextField
              name="RangeOfEffectiveness"
              label="Range of Effectiveness"
              inputProps={{ maxLength: 50 }}
            />
          </Grid>
          <Grid item xs={12}>
            <SearchPersonAutoComplete
              name="ptsPersonID"
              label="Person"
              defaultPersonId={ptsPersonID}
              onAddIconClick={() => setAddPersonDialogOpen(true)}
              showDetailsIcon={false}
            />
          </Grid>
          <Grid item xs={12}>
            <SearchAddressAutoComplete
              name="ptsAddressID"
              label="Search Address"
              defaultAddressId={ptsAddressID}
              onAddIconClick={() => setAddAddressDialogOpen(true)}
              showDetailsIcon={false}
            />
          </Grid>
          <Grid item xs={12}>
            <SearchVehicleAutoComplete
              name="ptsVehicleID"
              label="Search Vehicle"
              defaultVehicleId={ptsVehicleID}
              onSelectionChange={handleVehicleSelect}
              showAddIcon
              onAddIconClick={() => setAddVehicleDialogOpen(true)}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSTextField name="Plate" label="Plate" inputProps={{ maxLength: 25 }} />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect
              name="PlateState"
              label="Plate State"
              options={stateOptions}
              {...DICT_SELECT_PROPS}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSTextField name="OLN" label="OLN" inputProps={{ maxLength: 50 }} />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect
              name="OLNState"
              label="OLN State"
              options={stateOptions}
              {...DICT_SELECT_PROPS}
            />
          </Grid>
          <Grid item xs={12}>
            <RMSTextField name="Notification" label="Notes" multiline rows={4} />
          </Grid>
        </Grid>
      </FormProvider>

      {addPersonDialogOpen && (
        <AddPersonDialog
          open={addPersonDialogOpen}
          onClose={() => setAddPersonDialogOpen(false)}
          onPersonCreated={handlePersonCreated}
        />
      )}

      {addAddressDialogOpen && (
        <AddAddressDialog
          open={addAddressDialogOpen}
          onClose={() => setAddAddressDialogOpen(false)}
          onAddressCreated={handleAddressCreated}
        />
      )}

      {addVehicleDialogOpen && (
        <AddVehicleDialog
          open={addVehicleDialogOpen}
          onClose={() => setAddVehicleDialogOpen(false)}
          onVehicleCreated={handleVehicleCreated}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default PersonBulletinDialog;
